// Parallel components to /shared/attachments/, but modernized and for non-redux use-cases
import React, {createContext, useState, useContext, useEffect, useMemo, useRef} from 'react'
import {api, apiErrorMessage} from 'js/studios/shared/api'
import {classNames} from 'js/studios/shared/utils'
import {ProgressBar} from 'js/studios/shared/attachments/progress_bar'
import PdfSvg from 'images/brand2025/icons/pdf.svg'

export const AttachmentsContext = createContext()
AttachmentsContext.displayName = "AttachmentsContext"

// generate a temporary identifier for a new attachment being uploaded
function genUploadId() {
  return `upload:${Math.random()}`
}

// Provider component to provide an AttachmentsContext. Optionally can be initialized with a set of
// attachments.
export function AttachmentsProvider({
  attachments: initAttachments,
  imageGroup: initImgGroup = true, // optional, name for flexbox modal grouping. `true` is a special value that will auto-generate a unique string
  onChange, // optional, callback that receives full list of attachments on any change
  children,
}) {
  const [attachments, setAttachments] = useState(initAttachments || [])
  const imageGroup = useMemo(() => {
    if (initImgGroup === true) {
      return `grp-${Math.random()}`
    }
    return initImgGroup
  }, [initImgGroup])
  const ctxVal = useMemo(() => ({
    attachments,
    setAttachments,
    imageGroup,
    isUploading: attachments.some(a => a.isUpload),
    removeAttachment: attachment => {
      setAttachments(attachments => attachments.filter(a => a.id !== attachment.id))
    },
  }), [attachments, imageGroup])

  useEffect(() => initAttachments && setAttachments(initAttachments), [initAttachments])
  useEffect(() => onChange && onChange(attachments), [onChange, attachments])

  return <AttachmentsContext.Provider value={ctxVal}>{children}</AttachmentsContext.Provider>
}

function AttachmentsListInner({className, showIndices, editable, overlayFactory}) {
  const {attachments} = useContext(AttachmentsContext)

  return <ul className={classNames("attachments", className)}>
    {attachments.map((a, idx) => <Attachment key={a.id} attachment={a}
      index={showIndices ? idx + 1 : null} editable={editable} overlayFactory={overlayFactory} />)}
  </ul>
}

// Render a list of attachments. Can be used inside an <AttachmentsProvider> and will use
// attachments from the context, or can be provided an `attachments` prop for use outside a
// provider.
export function AttachmentsList({
  className,
  attachments: propAttachments, // attachments to render if used outside of an AttachmentsContext
  showIndices=false, // show the position of each attachment in a top-left circle marker
  editable=false, // allows removing attachments from list if true
  overlayFactory=null, // null will use the default, which is the lightbox expander link
}) {
  const
    ctx = useContext(AttachmentsContext),
    inner = <AttachmentsListInner {...{className, showIndices, editable, overlayFactory}}/>

  if (!ctx) {
    return <AttachmentsProvider attachments={propAttachments}>{inner}</AttachmentsProvider>
  } else {
    return inner
  }
}

const IMAGE_CONTENT_TYPES = ["image/jpeg", "image/png", "image/gif", "image/bmp"]
const NON_IMAGE_THUMBNAIL = `${window.cm.structureUrl}/static/library/images/job_new/attachment.gif`

// render a currently uploading unsaved attachment
function Upload({attachment, index, editable=false}) {
  const {removeAttachment} = useContext(AttachmentsContext) || {}
  const [imageSrc, setImageSrc] = useState(NON_IMAGE_THUMBNAIL)
  useEffect(() => {
    if (IMAGE_CONTENT_TYPES.includes(attachment.file.type)) {
      const reader = new FileReader()
      reader.onload = ev => setImageSrc(ev.target.result)
      reader.readAsDataURL(attachment.file)
    } else {
      setImageSrc(NON_IMAGE_THUMBNAIL)
    }
  }, [attachment])

  const onRemove = ev => {
    ev.preventDefault()
    removeAttachment(attachment)
  }

  const cssClass = classNames('upload_item attachment', {'error upload': !!attachment.error})
  return <li className={cssClass}>
    <div className="attachment_wrapper">
      {index ? <span className="index_marker" aria-label={`Attachment number ${index}`}>{index}</span> : null}
      <img src={imageSrc} alt={attachment.file.name} />
      {attachment.error
        ? <div className="outer_bar error"><span className="error_message">
          Upload failed{" "}
          <span className="material-icons" title={attachment.error} style={{fontSize: "15px", cursor: "help"}}>info</span>
        </span></div>
        : <ProgressBar progress={Math.round(attachment.uploadProgress * 100)} />}
    </div>
    {editable ? <a href="#" className="remove_attachment icon-cancel-circled" onClick={onRemove} aria-label="Remove attachment"></a> : null}
  </li>
}

function startFancybox() {
  if (window.cm.isRebrand) {
    return // rebrand app_base.js sets up SimpleBox to attach to existing data-fancybox markup
  } else {
    if (!window.cm.fancyboxWidget) { window.cm.fancyboxWidget = {initiated: true} }
    const fancyboxParams = window.cm.fancyboxParams || {
      openEffect: 'fade',
      closeEffect: 'fade',
      helpers: {thumbs: {width: 100, height: 100}},
    }
    window.$('[data-fancybox]').fancybox(fancyboxParams)
  }
}

// default overlay shows "expand" functionality and download
export function DefaultAttachmentOverlay({attachment, allowDownload=true, children}) {
  let {imageGroup} = useContext(AttachmentsContext) || {}
  useEffect(() => {
    if (window.cm?.fancyboxWidget && window.$?.fancybox?.update) {
      window.$.fancybox.update()
    } else {
      startFancybox()
    }
  }, [])

  const permitDownload = allowDownload && window.cm.studiosUser?.isStaff && attachment.originalDownloadUrl
  imageGroup = (imageGroup || "").toString().replace(":", "-")  // cleaning illegal chars

  const expanderLinkAttrs = {
    "data-fancybox": "", rel: imageGroup, "data-simplebox-thumb-href": attachment.smallUrl,
    "data-track-event-sp": "click_expand_image*browse",
  }

  return <div className={classNames("image_expander_overlay", {"multi-action": permitDownload || children})}>
    <a className="icon-resize-full" title="Expand image" href={attachment.videoUrl || attachment.fullUrl} {...expanderLinkAttrs}></a>
    {permitDownload && <a className="image_expander_download" title={`Download ${attachment.name}`} href={attachment.originalDownloadUrl}></a>}
    {children}
  </div>
}

function AttachmentImageContent({attachment, editable=false, overlayFactory}) {
  const {removeAttachment} = useContext(AttachmentsContext) || {}
  const overlay = useMemo(() => overlayFactory
    ? overlayFactory(attachment)
    : <DefaultAttachmentOverlay attachment={attachment} />,
    [attachment, overlayFactory])

  const onRemove = ev => {
    ev.preventDefault()
    removeAttachment(attachment)
  }

  return <>
    {overlay}
    {editable ? <a href="#" className="remove_attachment icon-cancel-circled" onClick={onRemove}></a> : null}
    <img src={attachment.smallUrl} alt={`image named ${attachment.name}`} />
  </>
}

function AttachmentNonImageContent({attachment, editable=false}) {
  const
    {removeAttachment} = useContext(AttachmentsContext) || {},
    onRemove = ev => { ev.preventDefault(); removeAttachment(attachment) },
    thumbnail = window.cm.studiosUser?.isStaff
      ? <img src={attachment.pdfUrl} alt="" role="presentation" />
      : <div className="attachment__thumb"><PdfSvg /></div>

  return <>
    {editable ? <a href="#" className="remove_attachment icon-cancel-circled" onClick={onRemove}></a> : null}
    <a href={attachment.fullUrl} download={attachment.filename} title={`View ${attachment.name}`}>
      {thumbnail}
    </a>
  </>
}

// Base attachment component. Can also be an in-progress upload.
export function Attachment({
  attachment, // the attachment, required
  index, // optional, number to display for attachment position
  className, // optional, is appended to default of "attachment" when set
  editable=false, // if true, can be removed from list
  overlayFactory, // optional, should be a function taking the attachment and returning a component
  tagName="li",
  children, // optional, additional content to insert into the wrapper after the <img />
}) {
  if (attachment.isUpload) { return <Upload attachment={attachment} index={index} editable={editable} /> }

  const content = attachment.isImage
    ? <AttachmentImageContent attachment={attachment} overlayFactory={overlayFactory} editable={editable} />
    : <AttachmentNonImageContent attachment={attachment} editable={editable} />

  const wrapperAttrs = {}
  if (attachment.videoUrl) { wrapperAttrs ["data-video-thumb"] = true }

  const TagName = tagName || "li"
  return <TagName className={classNames("attachment", className)}>
    <div className="attachment_wrapper" {...wrapperAttrs}>
      {index ? <span className="index_marker" aria-label={`Attachment number ${index}`}>{index}</span> : null}
      {content}
      {children}
    </div>
  </TagName>
}

// button to upload new attachment(s)
//
// Easiest to use inside an AttachmentsContext. Can also be used outside a context, but if you do
// you must specify an onChange handler or this component won't do anything useful.
//
// onChange will receive the upload object when it was created, when progress changes, and when the
// the upload completes or errors. When the upload completes successfully, onChange will receive the
// new attachment object with an added `uploadId` key so you can identify the upload it's replacing.
// If multiple files are being uploaded, onChange will be called for each one individually.
//
// onFocus/onBlur are "simulated" events: "focus" happens when the user clicks the upload button,
// "blur" happens when the files start uploading, or when the user cancels out of the file selection
// window. These handlers are not always passed an event argument, since there is no real underlying
// event.
export function UploadButton({className, multiple=true, onChange, children, onFocus, onBlur}) {
  const {setAttachments} = useContext(AttachmentsContext) || {}
  const inputRef = useRef()

  const
    uploadFile = async file => {
      const upload = {
        isUpload: true,
        id: genUploadId(),
        uploadProgress: 0,
        file: file,
      }
      setAttachments?.(curAttachments => curAttachments.concat([upload]))
      onChange?.(upload)

      const progressFn = progressPct => {
        setAttachments?.(curAttachments => curAttachments.map(a => {
          if (a.id === upload.id) {
            return {...a, uploadProgress: progressPct}
          } else {
            return a
          }
        }))
        onChange && onChange({...upload, uploadProgress: progressPct})
      }

      try {
        const resp = await api.uploadAttachment(file, progressFn)
        setAttachments?.(curAttachments => curAttachments.map(a => {
          return a.id === upload.id ? resp : a
        }))
        onChange && onChange({...resp, uploadId: upload.id})
      } catch (err) {
        const errMsg = apiErrorMessage(err)
        setAttachments?.(curAttachments => curAttachments.map(a => {
          if (a.id === upload.id) {
            return {...a, uploadProgress: 0, error: errMsg}
          } else {
            return a
          }
        }))
        onChange && onChange({...upload, error: errMsg})
      }
    },
    clickUploadInput = ev => {
      ev.preventDefault()
      inputRef.current?.click?.()
      onFocus?.()
    },
    onInputChange = ev => {
      Array.from(ev.target.files).forEach(f => uploadFile(f))
      onBlur?.()
    }

  useEffect(() => {
    if (inputRef.current && onBlur) {
      const node = inputRef.current
      node.addEventListener("cancel", onBlur)
      return () => node.removeEventListener("cancel", onBlur)
    }
  }, [onBlur])

  return <>
    <button type="button" className={className} onClick={clickUploadInput}>{children}</button>
    <input type="file" ref={inputRef} style={{display:"none", height: 0, width: 0}}
      multiple={multiple} onChange={onInputChange}
      />
  </>
}
